import { Table } from 'antd';
import { useDispatch } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { Link, useLoaderData, useRevalidator } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { GetProjectsSuccessResponse } from '../../../../../types/api/project/get-projects-success-response';
import { showLoadingAlertMessage } from '../../../../../../../utils/show-loading-alert-message/show-loading-alert-message';
import { networkCall } from '../../../../../../../utils/network-call/network-call';
import { showDefaultErrorMessages } from '../../../../../../../utils/show-default-error-message/show-default-error-messages';
import { AlertMessageState, clearAlertMessage, showAlertMessage } from '../../../../../../../redux/reducer/alert-message.reducer';
import { AlertMessages } from '../../../../../../../enums/alert-messages.enum';
import { getImageUrl } from '../../../../../../../utils/image-helper/get-image-url';
import { formatTimestampToDate } from '../../../../../../../utils/date/format-timestamp-to-date';
import useDebouncedValue from '../../../../../../../hooks/use-debonced-value';
import { setSelectedMenuItemKeys } from '../../../../../../../redux/reducer/antd-layout-info.reducer';
import { SearchBar } from '../../../../../../../components/search-bar/search-bar';
import { CMSButton } from '../../../../../../../components/cms/button/cms-button.';

type TableDataType = {
    id: string | number;
    name: string;
    created: number;
    modified: number;
};

type DeleteColumnProps = {
    project: TableDataType;
    revalidate: () => void;
};

const DeleteColumn = ({ project: { id }, revalidate }: DeleteColumnProps) => {
    const dispatch = useDispatch();

    const onClick = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();

        if(window.confirm('Biztosan törlöd a projektet?')) {
            showLoadingAlertMessage();

            const response = await networkCall(`/projects/${ id }`, {
                method: 'DELETE'
            });;

            const messageShowed = showDefaultErrorMessages(response);
            if(messageShowed) return;

            if(response?.status === 204) {
                revalidate();
                dispatch(showAlertMessage({
                    alertMessageType: 'success',
                    alertMessageText: 'Törlés sikeres.'
                }));
                return;
            }

            dispatch(clearAlertMessage());
            const alertMessageState: AlertMessageState = {
                alertMessageType: 'error',
                alertMessageText: AlertMessages.UnknownErrorTryAgain
            };

            if(response?.status === 404) {
                alertMessageState.alertMessageText = 'Törlés sikertelen, a törlendő projekt nem létezik.';
            }

            dispatch(showAlertMessage(alertMessageState));
        }

    }

    return (
        <a onClick={onClick}>
            <img src={getImageUrl('delete-icon2.png')} alt="delete-icon2" />
        </a>
    );
}

export const CompanyAdminProjectList = () => {
    const dispatch = useDispatch();
    const loaderData: any = useLoaderData();
    const revalidator = useRevalidator();
    
    const columns = [
        {
            title: 'Név',
            dataIndex: 'name',
            sorter: (a: TableDataType, b: TableDataType) => String(a.name).localeCompare(b.name)
        },
        {
            title: 'Létrehozva',
            sorter: (a: TableDataType, b: TableDataType) => a.created - b.created,
            render: (record: TableDataType) => {
                return formatTimestampToDate(record.created);
            }
        },
        {
            title: 'Utolsó módosítás',
            sorter: (a: TableDataType, b: TableDataType) => a.modified - b.modified,
            render: (record: TableDataType) => {
                return formatTimestampToDate(record.modified);
            }
        },
        {
            render: (record: TableDataType) => {
                return (
                    <Link className='pink-font' to={`/cms/company-admin/projects/edit/${ record.id }`}>
                        Megtekintés
                    </Link>
                );
            }
        },
        {
            render: (record: TableDataType) => {
                return (
                    <Link to={`/cms/company-admin/projects/${ record.id }/modules`}>
                        Megnyitás
                    </Link>
                );
            }
        },
        {
            title: 'Törlés',
            render: (record: TableDataType) => {
                return (
                    <DeleteColumn project={record} revalidate={revalidator.revalidate}/>
                );
            }
        }
    ];
    const [data, setData] = useState<TableDataType[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebouncedValue(searchTerm);

    const handleSearchBarChange = (value: string) => {
        setSearchTerm(value);
        setDebouncedSearchTerm(value);
    };

    useEffect(() => {
        if(loaderData.getProjectsResponse?.status === 200) {
            const getProjectsResponse = loaderData.getProjectsResponse as AxiosResponse<GetProjectsSuccessResponse>;
            const tableData: TableDataType[] = [];
            for (const project of getProjectsResponse.data.projects) {
                if (project.name.toLocaleLowerCase().includes(debouncedSearchTerm.toLocaleLowerCase().trim()))
                    tableData.push({
                        id: project.id,
                        name: project.name,
                        created: project.created,
                        modified: project.modified
                    });
            }

            setData(tableData);
            return;
        }
    }, [loaderData.getProjectsResponse, debouncedSearchTerm]);

    useEffect(() => {
        dispatch(setSelectedMenuItemKeys(['company-admin-projects']));
    }, [dispatch]);

    return (
        <>
            <div className="page-title">Projektek</div>
            <div className='d-flex justify-content-between gap-3 mb-4'>
                <SearchBar value={searchTerm} onChange={handleSearchBarChange} />
                <Link to='/cms/company-admin/projects/add'>
                    <CMSButton text='Új projekt hozzáadása' iconSrc={getImageUrl('project-add-icon.png')} />
                </Link>
            </div>

            <Table
                className='table-responsive data-table'
                columns={columns}
                dataSource={data}
                bordered
                pagination={
                    {
                        position: ['bottomRight'],
                        pageSize: 10
                    }
                }
            />

        </>
    );
};